import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import * as Icons from '../components/CategoryIcons';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

// Convert kebab-case ("heart-pulse", "id-card") or snake_case to PascalCase
// so it maps to lucide-react's exported PascalCase component names.
const toPascal = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
};

// Dynamic Icon Component — used for SUB-CATEGORY tiles (small tiles).
// Tries Lucide first (kebab or PascalCase), falls back to FileText.
const DynamicIcon = ({ name }) => {
  const key = LucideIcons[name] ? name : toPascal(name);
  const IconComponent = LucideIcons[key] || LucideIcons.FileText;
  return <IconComponent className="w-5 h-5 text-[#172554]" />;
};

// Icon renderer for MAIN CATEGORIES on the home page.
// Resolution order:
//   1. Legacy CategoryIcons SVG (e.g. "GstIcon", "InsuranceIcon") — kept for
//      backward compatibility with seed data.
//   2. Lucide icon by exact name ("HeartPulse", "IdCard").
//   3. Lucide icon after kebab/snake → PascalCase conversion
//      ("heart-pulse" → "HeartPulse").
//   4. LucideIcons.FileText as a safe fallback (never a raw browser icon).
const MainCategoryIcon = ({ name, className }) => {
  const Legacy = Icons[name];
  if (Legacy) return <Legacy className={className} />;
  const direct = LucideIcons[name];
  if (direct) {
    const C = direct;
    return <C className={className} />;
  }
  const pascalKey = toPascal(name);
  const pascal = LucideIcons[pascalKey];
  const C = pascal || LucideIcons.FileText;
  return <C className={className} />;
};

export default function Home() {
  const navigate = useNavigate();
  const [featuredServices, setFeaturedServices] = useState([]);
  const [mainServices, setMainServices] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [gcSettings, setGcSettings] = useState(null);
  const [hasGreenCard, setHasGreenCard] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Log a single visit per browser session — device flavour derived from UA;
    // city (if the user has picked one) sent for the Visitor Insights heatmap.
    (async () => {
      try {
        if (sessionStorage.getItem('kb_visit_tracked')) return;
        const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
        let city = null;
        const phone = localStorage.getItem('userPhone');
        if (phone) {
          try {
            const p = await axios.get(`${API_URL}/api/profile/${phone}`);
            city = (p.data && p.data.city) || null;
          } catch (e) { /* profile might not exist; that's fine */ }
        }
        axios.post(`${API_URL}/api/track-visit`, { device: isMobile ? 'Mobile' : 'Desktop', city }).catch(() => {});
        sessionStorage.setItem('kb_visit_tracked', '1');
      } catch (e) { /* ignore */ }
    })();

    // Fetch top 3 most recent announcements (regardless of active flag)
    axios.get(`${API_URL}/api/announcements`)
      .then(res => {
        const recent = (Array.isArray(res.data) ? res.data : []).slice(0, 3);
        setAnnouncements(recent);
      })
      .catch(err => console.error(err));

    // Fetch dynamic main categories
    axios.get(`${API_URL}/api/services`)
      .then(res => setMainServices(res.data))
      .catch(err => console.error(err));

    axios.get(`${API_URL}/api/subservices?featured=true`)
      .then(res => {
        setFeaturedServices(res.data.slice(0, 17)); // Limit to 17
      })
      .catch(err => console.error(err));

    // Fetch Green Card settings and user's active card
    axios.get(`${API_URL}/api/greencard/settings`)
      .then(res => setGcSettings(res.data))
      .catch(console.error);
    
    if (localStorage.getItem('userToken')) {
      axios.get(`${API_URL}/api/greencard/my-card`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
      }).then(res => {
        if (res.data && (res.data.status || '').toLowerCase() === 'active') {
          // Check if expired
          const validUntil = new Date(res.data.valid_until);
          if (!res.data.valid_until || validUntil >= new Date()) {
            setHasGreenCard(true);
          }
        }
      }).catch(() => {});
    }
  }, [API_URL]);

  const handleServiceClick = (service) => {
    if (!localStorage.getItem('userToken')) {
      sessionStorage.setItem('intended_dest', `/${service.slug}`);
      window.dispatchEvent(new CustomEvent('openAuthModal'));
      return;
    }
    navigate(`/${service.slug}`);
  };

  const handleSubServiceClick = (sub) => {
    const parentSvc = (mainServices || []).find(s => s.title === sub.category_name);
    const catSlug = parentSvc?.slug || encodeURIComponent(sub.category_name);
    const dest = `/${catSlug}/${sub.slug || encodeURIComponent(sub.title)}/form`;
    if (!localStorage.getItem('userToken')) {
      sessionStorage.setItem('intended_dest', dest);
      window.dispatchEvent(new CustomEvent('openAuthModal'));
      return;
    }
    navigate(dest);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans flex flex-col">
      <Header />

      {/* Breaking News / Announcements Banner — sticky at top so it doesn't push page content around on mobile */}
      {announcements && announcements.length > 0 && (
        <div
          data-testid="announcements-marquee"
          className="w-full bg-[#172554] border-b-4 border-[#F5B800] overflow-hidden sticky top-0 z-30 shadow-md"
        >
          <div className="flex items-center min-w-0 py-1 md:py-0">
            <div className="my-0 ml-1.5 md:ml-2 mr-2 md:mr-3 bg-[#F5B800] text-[#172554] font-bold text-[11px] md:text-base px-2.5 md:px-4 py-1 md:py-1.5 shrink-0 flex items-center gap-1.5 md:gap-2 z-10 shadow-md rounded-full border-2 border-[#172554]/70 leading-none whitespace-nowrap">
              <LucideIcons.BellRing className="w-3.5 h-3.5 md:w-5 md:h-5 animate-pulse shrink-0" />
              <span className="text-gujarati">અગત્યની માહિતી</span>
            </div>
            <div className="flex-1 overflow-hidden relative min-w-0 flex items-center">
              <div className="animate-[marquee-loop_22s_linear_infinite] inline-flex items-center py-1.5 md:py-2 will-change-transform">
                {[0, 1].map((dup) => (
                  <div key={dup} className="flex items-center shrink-0" aria-hidden={dup === 1}>
                    {announcements.map((a, i) => (
                      <React.Fragment key={`${dup}-${i}`}>
                        <span className="mx-10 md:mx-24 text-gujarati text-white font-medium text-xs md:text-base whitespace-nowrap">
                          {a.text}
                        </span>
                        <span className="text-[#F5B800] font-bold text-sm md:text-base select-none">|</span>
                      </React.Fragment>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area — desktop keeps a 1.5-inch (~144px) breathing room
          on each side so the wide 12-column and 18-column rows feel centered
          and balanced on the page. On mobile/tablet the max-w-7xl centered
          container is preserved so nothing gets cropped. */}
      <main className="flex-1 px-4 md:px-8 py-8 w-full max-w-7xl mx-auto lg:max-w-none lg:mx-36 lg:w-auto lg:px-0">
        
        {/* Mobile Green Card Promotional Banner */}
        {!hasGreenCard && (
          <div 
            role="button"
            tabIndex={0}
            onClick={() => navigate('/green-card')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/green-card')}
            className="md:hidden w-full bg-gradient-to-r from-green-700 to-green-600 rounded-xl shadow-md mb-6 p-4 cursor-pointer relative overflow-hidden flex flex-col gap-3"
            data-testid="mobile-greencard-promo-banner"
          >
            {/* Decorative Icon */}
            <LucideIcons.CreditCard className="absolute -right-4 -bottom-4 w-24 h-24 text-white/10 -rotate-12" />
            
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 overflow-hidden">
                <img src="/kagajbook-logo.png" alt="K" className="w-8 h-8 object-contain" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg leading-tight text-gujarati mb-1">કાગજબુક ગ્રીન કાર્ડ મેળવો!</h3>
                {gcSettings?.benefits && gcSettings.benefits.length > 0 && (
                  <ul className="text-white/90 text-[11px] leading-snug list-disc pl-3 text-gujarati">
                    {gcSettings.benefits.slice(0, 2).map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="flex justify-end relative z-10 mt-1">
              <button 
                data-testid="mobile-greencard-promo-btn"
                className="bg-[#F5B800] text-[#172554] font-bold text-sm px-4 py-2 rounded-full shadow-sm flex items-center gap-1 animate-btnPulse text-gujarati"
              >
                અત્યારે અરજી કરો <LucideIcons.ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Main Services Box */}
        <div className="bg-white border border-[#E2E8F0] flex flex-col w-full shadow-sm rounded-sm">
          <h2 className="text-center text-[#172554] font-bold text-xl py-3 border-b-2 border-[#F5B800] bg-[#FFF8DB] text-gujarati">
            કૃપા કરીને આપની મુખ્ય સેવા પસંદ કરો
          </h2>
          
          <div className="p-3 md:p-4 lg:p-5 w-full overflow-hidden">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 items-start w-full gap-x-1.5 gap-y-4 md:gap-x-2 lg:gap-x-1 pb-2">
              
              {mainServices && mainServices.length === 0 ? (
                <div className="col-span-full text-center py-4 text-[#64748B]">કોઈ સેવાઓ ઉપલબ્ધ નથી</div>
              ) : (
                (mainServices || []).map((service, idx) => (
                  <div
                    key={service.id || idx}
                    data-testid={`main-category-${service.id || idx}`}
                    className="flex flex-col items-center gap-2 md:gap-2.5 cursor-pointer group min-w-0"
                    onClick={() => handleServiceClick(service)}
                  >
                    <div className="h-11 w-11 md:h-14 md:w-14 lg:h-12 lg:w-12 flex items-center justify-center text-[#172554] group-hover:scale-110 transition-transform">
                      <MainCategoryIcon
                        name={service.icon_type}
                        className="w-8 h-8 md:w-10 md:h-10 lg:w-9 lg:h-9"
                      />
                    </div>
                    <span className="text-center text-xs md:text-sm lg:text-sm font-bold text-[#172554] text-gujarati leading-tight px-0.5 break-words">
                      {service.title}
                    </span>
                  </div>
                ))
              )}

            </div>
          </div>

          {/* Note Footer */}
          <div className="bg-[#FFF8DB] text-[#172033] border-t border-[#E2E8F0] p-3 text-center text-sm font-normal tracking-wide text-gujarati">
            નોંધ : દસ્તાવેજી પ્રક્રિયાઓ ની અરજીમા અરજદાર તથા અરજીને લાગુ સભ્યોએ રૂબરુ કચેરી અથવા લાગુ સ્થળ પર આવવુ પડી શકે છે.
          </div>
        </div>

        {/* Featured Services Row — up to 17 featured sub-categories + "View More"
            tile fit in one 18-column desktop row inside the same 144px-margin
            container. Mobile falls back to a 4/6/9-per-row grid so it wraps
            gracefully. */}
        {featuredServices && featuredServices.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] flex flex-col w-full shadow-sm rounded-sm mt-6">
            <h2 className="text-center text-[#172554] font-bold text-xl py-3 border-b-2 border-[#F5B800] bg-[#FFF8DB] text-gujarati">
              મુખ્ય ઉપયોગી સેવાઓ (Important Services)
            </h2>

            <div className="p-2 md:p-4 lg:p-4 w-full overflow-hidden">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-9 lg:grid-cols-[repeat(18,minmax(0,1fr))] items-start w-full gap-x-1 gap-y-3 md:gap-x-1.5 lg:gap-x-1 pb-2">

                {(featuredServices || []).slice(0, 17).map((sub, idx) => (
                  <div
                    key={idx}
                    data-testid={`featured-sub-${idx}`}
                    className="flex flex-col items-center gap-1.5 md:gap-2 cursor-pointer group min-w-0"
                    onClick={() => handleSubServiceClick(sub)}
                  >
                    <div className="h-10 w-10 md:h-12 md:w-12 lg:h-10 lg:w-10 flex items-center justify-center text-[#172554] group-hover:bg-[#FFF8DB] rounded-full transition-colors border border-transparent group-hover:border-[#FFC928]">
                      <DynamicIcon name={sub.icon_type} />
                    </div>
                    <span className="text-center text-[10px] md:text-xs lg:text-xs font-semibold text-[#172554] text-gujarati leading-tight px-0.5 line-clamp-2 break-words">
                      {sub.title}
                    </span>
                  </div>
                ))}

                {/* View More — 18th tile in the grid, navigates to /services */}
                <div
                  className="flex flex-col items-center gap-1.5 md:gap-2 cursor-pointer group min-w-0"
                  data-testid="featured-view-more"
                  onClick={() => navigate('/services')}
                >
                  <div className="h-10 w-10 md:h-12 md:w-12 lg:h-10 lg:w-10 flex items-center justify-center text-[#172554] bg-[#F8FAFC] group-hover:bg-[#FFF8DB] rounded-full transition-colors border border-[#E2E8F0] shadow-sm group-hover:border-[#FFC928]">
                    <LucideIcons.ArrowRight className="w-4 h-4 md:w-5 md:h-5 lg:w-4 lg:h-4 text-[#172554] group-hover:translate-x-1 transition-transform" strokeWidth={2.25} />
                  </div>
                  <span className="text-center text-[10px] md:text-xs lg:text-xs font-bold text-[#172554] text-gujarati leading-tight">
                    વધુ જુઓ
                  </span>
                </div>

              </div>
            </div>
          </div>
        )}
        {/* Statistics Banner */}
        <div className="bg-[#172554] w-full h-[72px] mt-6 rounded-sm shadow-sm flex items-center justify-between px-2 sm:px-6 md:px-12 border border-[#E2E8F0] overflow-hidden">
          <div className="flex-1 flex flex-col items-center justify-center">
            <span className="text-[#F5B800] font-bold text-xl md:text-2xl leading-none">૫૬,૬૬૩</span>
            <span className="text-white text-xs md:text-sm font-medium mt-1 text-gujarati">કુલ લાભાર્થી</span>
          </div>
          <div className="h-[40px] w-px bg-white/30"></div>
          <div className="flex-1 flex flex-col items-center justify-center">
            <span className="text-[#F5B800] font-bold text-xl md:text-2xl leading-none">૧૨,૩૫૪</span>
            <span className="text-white text-xs md:text-sm font-medium mt-1 text-gujarati">ગ્રીન કાર્ડધારક</span>
          </div>
          <div className="h-[40px] w-px bg-white/30"></div>
          <div className="flex-1 flex flex-col items-center justify-center">
            <span className="text-[#F5B800] font-bold text-xl md:text-2xl leading-none">૮૨,૪૩૧</span>
            <span className="text-white text-xs md:text-sm font-medium mt-1 text-gujarati">ઉપયોગકર્તા</span>
          </div>
        </div>


      </main>
      
      <Footer />
    </div>
  );
}